import { useGT } from 'gt-react';
import { workbookFilterSchema } from '@erp/shared-types';
import { useFilterDescription } from './WorksheetFilters';
import type {
  DescribedChange,
  WorkbookHistoryChange,
  WorkbookHistoryFilter,
  WorkbookHistoryItem,
} from './workbookHistory';

type Kind = WorkbookHistoryChange['kind'];
type Of<K extends Kind> = WorkbookHistoryChange & { kind: K };
type DescribeFilter = (filter: WorkbookHistoryFilter) => string;

function addedLabel(
  gt: ReturnType<typeof useGT>,
  item: WorkbookHistoryItem,
  name: string,
): string {
  const labels: Record<WorkbookHistoryItem, () => string> = {
    sheet: () =>
      gt('Added worksheet {name}', {
        name,
        context: 'workbook edit history step',
      }),
    source: () =>
      gt('Added data {name}', {
        name: name,
        context: 'workbook edit history step',
      }),
    file: () =>
      gt('Added file {name}', {
        name: name,
        context: 'workbook edit history step',
      }),
    output: () =>
      gt('Added output {name}', {
        name,
        context: 'workbook edit history step',
      }),
    instruction: () =>
      gt('Added instruction {name}', {
        name,
        context: 'workbook edit history step',
      }),
  };
  return labels[item]();
}

function removedLabel(
  gt: ReturnType<typeof useGT>,
  item: WorkbookHistoryItem,
  name: string,
): string {
  const labels: Record<WorkbookHistoryItem, () => string> = {
    sheet: () =>
      gt('Deleted worksheet {name}', {
        name,
        context: 'workbook edit history step',
      }),
    source: () =>
      gt('Deleted data {name}', {
        name,
        context: 'workbook edit history step',
      }),
    file: () =>
      gt('Deleted file {name}', {
        name,
        context: 'workbook edit history step',
      }),
    output: () =>
      gt('Deleted output {name}', {
        name,
        context: 'workbook edit history step',
      }),
    instruction: () =>
      gt('Deleted instruction {name}', {
        name,
        context: 'workbook edit history step',
      }),
  };
  return labels[item]();
}

/** An item with no name is an untitled Instruction. */
function untitledLabel(
  gt: ReturnType<typeof useGT>,
  kind: Of<'added' | 'removed' | 'moved' | 'edited'>['kind'],
): string {
  const labels = {
    added: () =>
      gt('Added an instruction', { context: 'workbook edit history step' }),
    removed: () =>
      gt('Deleted an instruction', { context: 'workbook edit history step' }),
    moved: () =>
      gt('Moved an instruction', { context: 'workbook edit history step' }),
    edited: () =>
      gt('Edited an instruction', { context: 'workbook edit history step' }),
  };
  return labels[kind]();
}

function itemLabel(
  gt: ReturnType<typeof useGT>,
  { kind, item, name }: Of<'added' | 'removed' | 'moved' | 'edited'>,
): string {
  if (name === null) return untitledLabel(gt, kind);
  if (kind === 'added') return addedLabel(gt, item, name);
  if (kind === 'removed') return removedLabel(gt, item, name);
  return kind === 'moved'
    ? gt('Moved {name}', { name: name, context: 'workbook edit history step' })
    : gt('Edited {name}', {
        name: name,
        context: 'workbook edit history step',
      });
}

function workbookLabel(
  gt: ReturnType<typeof useGT>,
  kind: Of<
    'renamed-workbook' | 'edited-instructions' | 'reordered' | 'edited-workbook'
  >['kind'],
): string {
  if (kind === 'renamed-workbook')
    return gt('Renamed the workbook', {
      context: 'workbook edit history step',
    });
  if (kind === 'edited-instructions')
    return gt('Edited the workbook instructions', {
      context: 'workbook edit history step',
    });
  if (kind === 'reordered')
    return gt('Reordered the workbook items', {
      context: 'workbook edit history step',
    });
  return gt('Edited the workbook', { context: 'workbook edit history step' });
}

function renameLabel(
  gt: ReturnType<typeof useGT>,
  change: Of<'renamed' | 'column-renamed'>,
): string {
  return change.kind === 'renamed'
    ? gt('Renamed {from} to {to}', {
        from: change.from,
        to: change.to,
        context: 'workbook edit history step',
      })
    : gt('Renamed column {from} to {to}', {
        from: change.from,
        to: change.to,
        context: 'workbook edit history step',
      });
}

type ColumnChange = Of<
  | 'column-added'
  | 'column-removed'
  | 'formula-changed'
  | 'column-type-changed'
  | 'column-format-changed'
  | 'column-edited'
>;
function columnLabel(
  gt: ReturnType<typeof useGT>,
  change: ColumnChange,
): string {
  const values = {
    column: change.column,
    sheet: change.sheet,
    context: 'workbook edit history step',
  };
  switch (change.kind) {
    case 'column-added':
      return gt('Added column {column} to {sheet}', values);
    case 'column-removed':
      return gt('Deleted column {column} from {sheet}', values);
    case 'formula-changed':
      return gt('Changed formula of {column}', values);
    case 'column-type-changed':
      return gt('Changed type of {column}', values);
    case 'column-format-changed':
      return gt('Changed number format of {column}', values);
    case 'column-edited':
      return gt('Edited column {column} in {sheet}', values);
  }
}

type LookupChange = Of<'lookup-added' | 'lookup-removed' | 'lookup-changed'>;
function lookupLabel(
  gt: ReturnType<typeof useGT>,
  change: LookupChange,
): string {
  const values = {
    column: change.column,
    sheet: change.sheet,
    context: 'workbook edit history step',
  };
  if (change.kind === 'lookup-added')
    return gt('Added lookup {column} to {sheet}', values);
  if (change.kind === 'lookup-removed')
    return gt('Deleted lookup {column} from {sheet}', values);
  return gt('Changed lookup {column}', values);
}

type SheetChange = Of<
  | 'columns-reordered'
  | 'sort-cleared'
  | 'pivot-changed'
  | 'cells-edited'
  | 'table-changed'
  | 'sorted'
>;
function sheetLabel(gt: ReturnType<typeof useGT>, change: SheetChange): string {
  if (change.kind === 'sorted')
    return change.direction === 'desc'
      ? gt('Sorted {sheet} by {column}, descending', {
          sheet: change.sheet,
          column: change.column,
          context: 'workbook edit history step',
        })
      : gt('Sorted {sheet} by {column}, ascending', {
          sheet: change.sheet,
          column: change.column,
          context: 'workbook edit history step',
        });
  const values = { sheet: change.sheet, context: 'workbook edit history step' };
  if (change.kind === 'columns-reordered')
    return gt('Reordered columns in {sheet}', values);
  if (change.kind === 'sort-cleared')
    return gt('Cleared the sort of {sheet}', values);
  if (change.kind === 'cells-edited')
    return gt('Edited cells of {sheet}', values);
  if (change.kind === 'table-changed')
    return gt('Changed the result table of {sheet}', values);
  return gt('Changed pivot table {sheet}', values);
}

function cellLabel(
  gt: ReturnType<typeof useGT>,
  change: Of<'cell-edited' | 'cell-cleared'>,
): string {
  const values = {
    sheet: change.sheet,
    cell: change.cell,
    context: 'workbook edit history step',
  };
  return change.kind === 'cell-edited'
    ? gt('Edited cell {cell} of {sheet}', values)
    : gt('Cleared cell {cell} of {sheet}', values);
}

function filterLabel(
  gt: ReturnType<typeof useGT>,
  change: Of<'filtered' | 'filter-removed'>,
  describeFilter: DescribeFilter,
): string {
  return change.kind === 'filtered'
    ? gt('Filtered {name} ({filter})', {
        name: change.name,
        filter: describeFilter(change.filter),
        context: 'workbook edit history step',
      })
    : gt('Removed the filter on {column} from {name}', {
        name: change.name,
        column: change.column,
        context: 'workbook edit history step',
      });
}

function isKind<K extends Kind>(
  change: WorkbookHistoryChange,
  kinds: readonly K[],
): change is Of<K> {
  return (kinds as readonly Kind[]).includes(change.kind);
}

/** The history's label for a described Definition edit: a verb and its object. */
export function workbookHistoryLabel(
  gt: ReturnType<typeof useGT>,
  change: WorkbookHistoryChange,
  describeFilter: DescribeFilter,
): string {
  if (isKind(change, ['added', 'removed', 'moved', 'edited'] as const))
    return itemLabel(gt, change);
  if (isKind(change, ['renamed', 'column-renamed'] as const))
    return renameLabel(gt, change);
  if (isKind(change, ['filtered', 'filter-removed'] as const))
    return filterLabel(gt, change, describeFilter);
  if (
    isKind(change, [
      'lookup-added',
      'lookup-removed',
      'lookup-changed',
    ] as const)
  )
    return lookupLabel(gt, change);
  if (
    isKind(change, [
      'columns-reordered',
      'sort-cleared',
      'pivot-changed',
      'cells-edited',
      'table-changed',
      'sorted',
    ] as const)
  )
    return sheetLabel(gt, change);
  if (isKind(change, ['cell-edited', 'cell-cleared'] as const))
    return cellLabel(gt, change);
  if (
    isKind(change, [
      'column-added',
      'column-removed',
      'formula-changed',
      'column-type-changed',
      'column-format-changed',
      'column-edited',
    ] as const)
  )
    return columnLabel(gt, change);
  if (
    isKind(change, [
      'renamed-workbook',
      'edited-instructions',
      'reordered',
      'edited-workbook',
    ] as const)
  )
    return workbookLabel(gt, change.kind);
  return gt('Edited the workbook', {
    context: 'workbook edit history step',
  });
}

/** A filter described as the Filters panel describes it, else by its column. */
function useHistoryFilterDescription(): DescribeFilter {
  const describe = useFilterDescription();
  return (filter) => {
    const operator = workbookFilterSchema.shape.operator.safeParse(
      filter.operator,
    );
    return operator.success
      ? describe({
          column: filter.column,
          operator: operator.data,
          value: filter.value,
          ...(filter.values && { values: [...filter.values] }),
        })
      : filter.column;
  };
}

/** A described Definition edit as a step of the Workbook's edit history. */
export function useWorkbookHistoryStep() {
  const gt = useGT();
  const describeFilter = useHistoryFilterDescription();
  return ({ change, group }: DescribedChange) => ({
    label: workbookHistoryLabel(gt, change, describeFilter),
    group,
  });
}
